import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

export default function SetupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('Admin User');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [adminExists, setAdminExists] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/setup-check`);
        if (res.data.admin_exists) {
          setAdminExists(true);
          setAdminEmail(res.data.admin_email || '');
          setEmail(res.data.admin_email || '');
        }
      } catch {
        setError('Could not connect to server. Please check the deployment.');
      } finally {
        setLoading(false);
      }
    };
    check();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/setup`,
        { email, password, name },
        { withCredentials: true }
      );
      setSuccess(true);
      await checkAuth();
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Setup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8F5F0] to-[#F0F9F6]">
        <p className="text-[#5A7A7A]">Checking setup status...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8F5F0] to-[#F0F9F6] p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="setup-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#5BA4A4] rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <CardTitle className="text-2xl font-bold text-[#2D4A4A]">
            {adminExists ? 'Reset Admin Account' : 'Admin Setup'}
          </CardTitle>
          <CardDescription className="text-[#5A7A7A]">
            {adminExists
              ? `Current admin: ${adminEmail}. Reset credentials below.`
              : 'Create your admin account to get started.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-2" data-testid="setup-success">
              <div className="text-green-600 font-semibold text-lg">
                {adminExists ? 'Admin account reset!' : 'Admin account created!'}
              </div>
              <p className="text-[#5A7A7A]">Redirecting to dashboard...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="setup-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Admin User"
                  required
                  data-testid="setup-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  data-testid="setup-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a strong password"
                  required
                  minLength={6}
                  data-testid="setup-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                disabled={submitting}
                data-testid="setup-submit-button"
              >
                {submitting
                  ? (adminExists ? 'Resetting...' : 'Creating Account...')
                  : (adminExists ? 'Reset & Login' : 'Create Admin Account')}
              </Button>
              <p className="text-center text-sm text-[#5A7A7A]">
                Already logged in?{' '}
                <a href="/login" className="text-[#5BA4A4] hover:underline">Go to login</a>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
